import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';

import { useNavigate } from 'react-router-dom';

import VideoPallet from '../../../../utils/VideoPallet';
import ColorPallet from '../../../../utils/ColorPallet';

function HeroSection() {
  const navigate = useNavigate();

  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);

  const handleNavigatetoAbout = () => {
    navigate('/about-hairstudio');
  }

  const handleNavigatetoServices = () => {
    navigate('/services-hairstudio');
  }

  useEffect(() => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  }, [videoRef.current]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
            })
            .catch(error => {
              console.error("Error playing video:", error);
              setError("Failed to play video");
            });
        }
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      setIsLoading(false);
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };
  
  return (
  <div className="container mx-auto px-6 pt-32">
      <div className="flex flex-col lg:flex-row object- items-center gap-12">
        {/* Left side - Image */}
        <div className="lg:w-1/2">
          <div 
            className="relative w-full aspect-square rounded-xl overflow-hidden group bg-black"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              </div>
            )}

            {error && (
              <div className="absolute inset-0 flex items-center justify-center text-white bg-black/50">
                <p>{error}</p>
              </div>
            )}

            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              playsInline
              controls={false}
              preload="auto"
              onClick={togglePlay}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleVideoEnd}
            >
              <source src={VideoPallet.Moments_1} type="video/quicktime" />
              <source src={VideoPallet.Moments_1} type="video/mp4" />
              Your browser does not support the video tag.
            </video>

            {/* Play/Pause Button */}
            <button
              onClick={togglePlay}
              className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                bg-black/50 hover:bg-black/70 p-4 rounded-full transition-all duration-300
                ${(isHovered || !isPlaying) ? 'opacity-100' : 'opacity-0'}
              `}
            >
              {isPlaying ? (
                <Pause className="w-6 h-6 text-white" />
              ) : (
                <Play className="w-6 h-6 text-white" />
              )}
            </button>

            {/* Video Controls Overlay */}
            <div 
              className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent
                px-4 pb-4 pt-12 transition-opacity duration-300 
                ${isHovered ? 'opacity-100' : 'opacity-0'}`}
            >
              {/* Progress Bar */}
              <div 
                className="relative w-full h-1 bg-white/30 rounded cursor-pointer mb-2"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const percentage = x / rect.width;
                  if (videoRef.current) {
                    videoRef.current.currentTime = percentage * videoRef.current.duration;
                  }
                }}
              >
                <div 
                  className="absolute top-0 left-0 h-full bg-white/90 rounded"
                  style={{ width: `${(currentTime / duration) * 100}%` }}
                />
                <div 
                  className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md"
                  style={{ left: `${(currentTime / duration) * 100}%`, transform: 'translate(-50%, -50%)' }}
                />
              </div>

              {/* Time Display */}
              <div className="flex justify-between text-white text-sm font-medium">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Content */}
        <div className="lg:w-1/2">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-transparent bg-clip-text" style={{ backgroundColor: ColorPallet.primaryDarkBrown }}>
            Hairstudio Cowok Dan Cewek
          </h2>
          <h1 className="text-5xl md:text-3xl font-bold mb-6">
            Ubah Penampilan Anda Dengan Gaya Rambut Modern
          </h1>
          <p className="text-black text-lg mb-8">
            Welcome to Hairstudio cowok dan cewek. From trendy haircuts to stylish
            color treatments, we offer a range of services tailored to meet the needs of
            every individual.
          </p>
          <div className="flex flex-wrap gap-4 mt-40">
            <button onClick={() => handleNavigatetoAbout()} className="px-8 py-3 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold hover:opacity-90 transform hover:scale-110 ease-in duration-300">
              About us
            </button>
            <button onClick={() => handleNavigatetoServices()} className="px-8 py-3 rounded-full border border-wh border-white hover:border-transparent text-black font-semibold hover:bg-gradient-to-r from-blue-600 to-purple-600 hover:text-white transform hover:scale-110 ease-in duration-300 transition-colors">
              Services
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

const formatTime = (timeInSeconds) => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export default HeroSection